import type { ParticipantTask, ParticipantTaskStatus } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import {
  constructZodLiteralUnionType,
  type DateTime,
  dateTimeSchema,
  P,
  z,
} from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  daysToRecurrenceOption,
  type RecurrenceOption,
  recurrenceOptions,
} from '../../components/forms/HookedTaskRecurrenceSelect';
import { HookedSubmitButton } from '../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../components/generic/Modal';
import { useUpdateParticipantTaskMutation } from '../../graphql/hooks/useUpdateParticipantTaskMutation';
import { useAppContext } from '../../hooks/useAppContext';
import { zodResolver } from '../../utils/zod';
import { ParticipantTaskForm } from './ParticipantTaskForm';

type FormValues = {
  name?: string;
  status?: ParticipantTaskStatus;
  dueDate?: DateTime | null;
  requiresDueDate?: boolean | null;
  labelIds?: string[];
  assigneeIds?: string[];
  recurrence?: RecurrenceOption | null;
};

const schema: z.ZodSchema<FormValues> = z.object({
  name: z.string().min(1, 'Task name is required'),
  requiresDueDate: z.boolean(),
  dueDate: dateTimeSchema.nullable(),
  labelIds: z.array(z.string()),
  assigneeIds: z.array(z.string()),
  recurrence: constructZodLiteralUnionType(recurrenceOptions.map((option) => option.raw)),
});

interface ParticipantNoteEditModalProps {
  participantTask: ParticipantTask;
}

export const ParticipantTaskEditModal = createModal<ParticipantNoteEditModalProps>((props) => {
  const { participantTask, modal } = props;
  const { errorToast, successToast, organization } = useAppContext();

  const { hide, remove, visible } = modal;

  const mutation = useUpdateParticipantTaskMutation();

  const defaultValues = React.useMemo(() => {
    return {
      name: participantTask.name,
      status: participantTask.status,
      requiresDueDate: participantTask.requiresDueDate,
      dueDate: participantTask.dueDate,
      labelIds: participantTask.labels.map((label) => label.id),
      assigneeIds: participantTask.assignees.map((assignee) => assignee.id),
      recurrence: participantTask.recurrence?.intervalDays
        ? daysToRecurrenceOption(participantTask.recurrence.intervalDays)
        : 'none',
    };
  }, [participantTask]);

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'all',
    resolver: zodResolver(schema),
  });

  const { handleSubmit } = methods;

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      const request = P.pickBy(values, (value, key) => {
        return !P.isEqual(value, defaultValues[key]);
      });

      try {
        const recurrence =
          values.recurrence === 'none'
            ? null
            : recurrenceOptions.find((option) => option.raw === values.recurrence);

        await mutation.mutateAsync({
          organizationId: organization.id,
          participantTaskId: participantTask.id,
          participantId: participantTask.participantId,
          request: {
            ...P.deepTrim(request),
            recurrence: recurrence ? { intervalDays: recurrence.intervalDays } : null,
          },
        });
        successToast('Task updated successfully');
        hide();
      } catch (error) {
        errorToast(`Error updating task: ${error.message}`);
      }
    },
    [errorToast, hide, mutation, organization.id, defaultValues, participantTask, successToast],
  );

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove}>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Edit task'
                subtitle='Please complete the following to proceed'
              />
              <ChakraV3.Dialog.Body>
                <ParticipantTaskForm participantId={participantTask.participantId} />
              </ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton>Save</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
